import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import {
  useGetEmployersOffboardingAllEmployeesToOffboard,
  useGetEmployersOffboardingEmployeesToOffboard,
} from 'utils/web_api'

import { type EmployeeInfo, useBulkOffboarding } from './BulkOffboardingContext'

const PAGE_SIZE = 10

export function useSelectEmployees () {
  const {
    selectedEmployeeIds,
    toggleEmployeeSelection,
    selectAllOnPage,
    deselectAllOnPage,
    selectAllEmployees,
    clearAllSelections,
    setEmployees,
    setTotalEmployeeCount,
    totalEmployeeCount,
    setCurrentStep,
    setCurrentEmployeeIndex,
    setEmployeeOffboardingData,
    getSelectedEmployees,
    isConnectedToPayroll,
  } = useBulkOffboarding()

  const [pageIndex, setPageIndex] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery] = useDebounceValue(searchQuery, 300)
  const [disableFiltering, setDisableFiltering] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [totalRows, setTotalRows] = useState(0)

  const clearAllSelectionsRef = useRef(clearAllSelections)
  clearAllSelectionsRef.current = clearAllSelections

  // Reset to first page and clear selections when filters change
  useEffect(() => {
    setPageIndex(0)
    clearAllSelectionsRef.current()
  }, [debouncedSearchQuery, statusFilter])

  // Build filter params for API calls
  const filterParams = useMemo(() => {
    const params: Record<string, unknown> = {
      disable_filtering: disableFiltering,
    }
    if (debouncedSearchQuery) {
      params.search = debouncedSearchQuery
    }
    if ((disableFiltering && isConnectedToPayroll) || statusFilter === 'on_leave') {
      params.show_muted = true
    }
    if (statusFilter === 'on_leave') {
      params.show_hidden = true
    }
    if (statusFilter) {
      params.status_filter = statusFilter
    }
    return params
  }, [debouncedSearchQuery, disableFiltering, isConnectedToPayroll, statusFilter])

  // Fetch paginated data for the table
  const { data: paginatedData, isLoading } = useGetEmployersOffboardingEmployeesToOffboard({
    'page[number]': pageIndex + 1,
    'page[size]': PAGE_SIZE,
    ...filterParams,
  })

  // Fetch all employees in background for "select all" and downstream steps
  // This query now respects search and status filters
  const { data: allEmployees, isSuccess: hasLoadedAllEmployees } = useGetEmployersOffboardingAllEmployeesToOffboard(
    filterParams
  )

  // Update employees in context when all data is loaded
  // When filters are active, this will be the filtered set
  useEffect(() => {
    if (allEmployees) {
      setEmployees(allEmployees as EmployeeInfo[])
      // Count selectable employees - include hidden when filtering by "on_leave"
      const selectableEmployees = statusFilter === 'on_leave'
        ? (allEmployees as EmployeeInfo[])
        : (allEmployees as EmployeeInfo[]).filter(e => !e.is_hidden)
      setTotalEmployeeCount(selectableEmployees.length)
    }
  }, [allEmployees, setEmployees, setTotalEmployeeCount, statusFilter])

  const currentPageEmployees = (paginatedData?.data ?? []) as EmployeeInfo[]
  const currentPageIds = useMemo(() => currentPageEmployees.map(e => e.id), [currentPageEmployees])
  const currentPageSelectableIds = useMemo(
    () => {
      if (statusFilter === 'on_leave') {
        return currentPageEmployees.map(e => e.id)
      }
      return currentPageEmployees.filter(e => !e.is_hidden).map(e => e.id)
    },
    [currentPageEmployees, statusFilter]
  )
  const selectedCount = selectedEmployeeIds.size

  // Cache totalRows so pagination doesn't momentarily see 0 when a new page is loading
  useEffect(() => {
    const nextTotal = paginatedData?.pagination?.total_records
    if (typeof nextTotal === 'number') {
      setTotalRows(nextTotal)
    }
  }, [paginatedData?.pagination?.total_records])

  // When filters are active, use totalRows from paginated API for immediate correct count.
  // Otherwise use totalEmployeeCount from all_employees.
  const filtersActive = !!(debouncedSearchQuery || statusFilter)
  const selectableCount = filtersActive ? totalRows : totalEmployeeCount

  const allOnPageSelected = useMemo(() => {
    if (currentPageSelectableIds.length === 0) return false
    return currentPageSelectableIds.every(id => selectedEmployeeIds.has(id))
  }, [currentPageSelectableIds, selectedEmployeeIds])

  const someOnPageSelected = useMemo(() => {
    if (allOnPageSelected) return false
    return currentPageSelectableIds.some(id => selectedEmployeeIds.has(id))
  }, [currentPageSelectableIds, selectedEmployeeIds, allOnPageSelected])

  const allEmployeesSelected =
    hasLoadedAllEmployees && selectedCount === selectableCount && selectableCount > 0

  const handleSelectAllOnPage = useCallback(() => {
    if (allOnPageSelected) {
      deselectAllOnPage(currentPageIds)
    } else {
      selectAllOnPage(currentPageSelectableIds)
    }
  }, [allOnPageSelected, currentPageIds, currentPageSelectableIds, selectAllOnPage, deselectAllOnPage])

  const handleNext = useCallback(() => {
    const selected = getSelectedEmployees()
    if (selected.length === 0) return

    const todayDateString = new Date().toISOString().split('T')[0]

    selected.forEach(employee => {
      setEmployeeOffboardingData(employee.id, {
        employeeId: employee.id,
        personalEmail: employee.personal_email ?? '',
        finalDayOfEmployment: employee.end_date ?? todayDateString,
        cobraEligible: null,
      })
    })

    setCurrentEmployeeIndex(0)
    setCurrentStep('details')
  }, [getSelectedEmployees, setEmployeeOffboardingData, setCurrentEmployeeIndex, setCurrentStep])

  const resetFiltering = useCallback((showAll: boolean) => {
    setDisableFiltering(showAll)
    if (!showAll) {
      setStatusFilter(null)
    }
    setPageIndex(0)
    clearAllSelections()
  }, [clearAllSelections])

  return {
    pageIndex,
    setPageIndex,
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    disableFiltering,
    statusFilter,
    setStatusFilter,
    isLoading,
    hasLoadedAllEmployees,
    currentPageEmployees,
    totalRows,
    selectedCount,
    isConnectedToPayroll,
    allOnPageSelected,
    someOnPageSelected,
    allEmployeesSelected,
    totalEmployeeCount,
    selectableCount,
    isEmployeeSelected: useCallback((id: string) => selectedEmployeeIds.has(id), [selectedEmployeeIds]),
    toggleEmployeeSelection,
    clearAllSelections,
    handleSelectAllOnPage,
    handleSelectAllEmployees: useCallback(() => {
      selectAllEmployees(statusFilter === 'on_leave')
    }, [statusFilter, selectAllEmployees]),
    handleNext,
    handleToggleFiltering: useCallback(() => resetFiltering(!disableFiltering), [resetFiltering, disableFiltering]),
    handleShowAllEmployees: useCallback(() => resetFiltering(true), [resetFiltering]),
    PAGE_SIZE,
  }
}
